import { useRuntimeConfig } from '#app'
import { CraftUtil } from '../util/craft-util'
import { FetchError } from 'ofetch'

/**
 * Composable for making requests to the Craft CMS API.
 *
 * Architecture:
 * - SSR: Fetches directly from the CMS using credentials from CloudFront headers (no extra hop).
 * - CSR: Fetches via same-origin Nuxt server proxy routes (/api/graphql, /api/cms/*),
 *        which inject the bearer token server-side. The token never reaches the browser.
 *
 * CMS URLs (adminUrl, craftUrl) are transferred from SSR → CSR via useState (no secrets).
 * CSRF protection: Tokens are fetched client-side via /api/csrf-refresh on app boot (csrf.client.ts).
 *                  Double-submit cookie pattern guards proxy routes via csrf middleware.
 *                  On 403 (expired/missing token), auto-refreshes token and retries (once).
 */
export function useGraphQL() {
    const runtimeConfig = useRuntimeConfig()
    const nuxtApp = useNuxtApp()

    // CSRF token for proxy requests (fetched by csrf.client.ts plugin on app boot, validated by csrf middleware)
    const csrfToken = useState<string>('csrf-token')

    // SSR-only: full config with token for direct CMS access
    let serverConfig: CraftCmsConfig | null = null

    if (import.meta.server) {
        const headers = nuxtApp.ssrContext.event.node.req.headers
        serverConfig = CraftUtil.getServerAuthConfig(runtimeConfig, headers)

        if (!serverConfig.endpoint || !serverConfig.token) {
            throw createError({
                statusCode: 500,
                statusMessage: 'GraphQL endpoint or token missing.',
            })
        }
    }

    // Transfer non-secret CMS URLs from SSR → CSR via useState (replaces the cookie approach)
    const adminUrl = useState<string>('cms-admin-url', () => {
        return serverConfig ? replace(serverConfig.endpoint, '/api', '/admin') : ''
    })
    const craftUrl = useState<string>('cms-craft-url', () => {
        return serverConfig ? replace(serverConfig.endpoint, '/api', '') : ''
    })
    const cmsApiUrl = useState<string>('cms-api-url', () => {
        return serverConfig?.endpoint || ''
    })

    /**
     * Refresh CSRF token from server and update local state.
     * Called when a CSR request returns 403 Forbidden (token expired/missing).
     */
    const refreshCsrfToken = async () => {
        try {
            const response = await $fetch<{ token: string }>('/api/csrf-refresh')
            csrfToken.value = response.token
        } catch (error) {
            console.error('Failed to refresh CSRF token:', error)
        }
    }

    /**
     * $fetch wrapper that retries once on 403 after refreshing the CSRF token.
     * Used for client-only requests (server === false).
     */
     
    const fetchWithCsrfRetry = async (url: string, fetchOptions: Record<string, any>) => {
        try {
            return await $fetch(url, fetchOptions)
        } catch (error: unknown) {
            if (error instanceof FetchError && error.status === 403) {
                await refreshCsrfToken()
                fetchOptions.headers['X-CSRF-Token'] = csrfToken.value
                return await $fetch(url, fetchOptions)
            }

            throw error
        }
    }

    /**
     * useFetch wrapper that retries once on 403 after refreshing the CSRF token.
     * Used for SSR-hydrated requests. Returns { data, error } like useFetch.
     */
     
    const useFetchWithCsrfRetry = async (url: string, fetchOptions: Record<string, any>) => {
        const { data, error } = await useFetch(url, fetchOptions)

        if (error.value?.statusCode === 403) {
            await refreshCsrfToken()

            const { data: retryData, error: retryError } = await useFetch(url, {
                ...fetchOptions,
                headers: {
                    ...fetchOptions.headers,
                    'X-CSRF-Token': csrfToken.value,
                },
            })

            return { data: retryData, error: retryError }
        }


        return { data, error }
    }

    const query = async ({ query, variables = {}, ...options }) => {
        const { isPreview, previewToken, livePreviewToken, previewTimestamp } = usePreview()

        const beforeFetchStamp = Date.now() / 1000

        const headers: Record<string, string> = {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
        }

        if (isPreview.value) {
            headers['X-Craft-Token'] = previewToken.value
            headers['X-Craft-Preview-Token'] = livePreviewToken.value
            //add the previewTimestamp to the key
            if (options.key) {
                options.key += `-${previewTimestamp.value}`
            }
        }

        // SSR: fetch directly from CMS (credentials available from headers, no proxy hop)
        if (import.meta.server && serverConfig) {
            headers['Authorization'] = `Bearer ${serverConfig.token}`

            const queryOptions = {
                key: options?.key,
                method: 'POST' as const,
                headers,
                body: JSON.stringify({ query, variables }),
            }

            // if server = false, skip useFetch on SSR (will run client-side only)
            if (options.server === false) {
                return null
            }

            const { data, error } = await useFetch(serverConfig.endpoint, queryOptions)

            if (error.value) {
                // Log the original error for debugging purposes
                console.error('GraphQL request failed', error.value)

                throw createError({
                    statusCode: error.value.statusCode ?? 502,
                    statusMessage: error.value.message ?? 'GraphQL request failed',
                })
            }

            if (data.value?.errors) {
                if (import.meta.dev) {
                    throw createError({
                        statusCode: 500,
                        statusMessage: 'GraphQL Request returned partial errors',
                        data: data.value?.errors
                    })
                } else {
                    // fail silently in production but log the errors for debugging
                    console.error('GraphQL Request returned partial errors', data.value?.errors)
                }
            }

            console.groupCollapsed(`${options.key} query in`, `${(Date.now() / 1000 - beforeFetchStamp).toFixed(2)}s`)
            console.groupEnd()

            return data.value?.data
        }

        // CSR: fetch via same-origin server proxy (token stays server-side)
        headers['X-CSRF-Token'] = csrfToken.value

        const proxyOptions = {
            key: options?.key,
            method: 'POST' as const,
            headers,
            body: JSON.stringify({ query, variables }),
        }

        // if server = false, use $fetch (client-only, no SSR hydration)
        if (options.server === false) {
            const response = await fetchWithCsrfRetry('/api/graphql', proxyOptions)
            return response.data
        }

        const { data } = await useFetchWithCsrfRetry('/api/graphql', proxyOptions)

        console.groupCollapsed(`${options.key} query in`, `${(Date.now() / 1000 - beforeFetchStamp).toFixed(2)}s`)
        if (import.meta.client) {
            console.log(query)
            console.log(variables)
        }
        console.groupEnd()

        return data.value?.data
    }

    const cmsQuery = async ({ uri, variables = {}, routeQuery = null, ...options }) => {
        const { isPreview, previewToken, livePreviewToken, previewTimestamp } = usePreview(routeQuery)

        if (isPreview.value) {
            // Needed for cache busting in preview mode
            const separator = uri.includes('?') ? '&' : '?'
            uri += `${separator}previewTimestamp=${previewTimestamp.value}`

            //add the previewTimestamp to the key
            if (options.key) {
                options.key += `-${previewTimestamp.value}`
            }
        } else {
            // If a `routeQuery` was provided by middleware, use it to check for `no-cache`.
            let rq = routeQuery
            if (!rq) {
                const route = useRoute()
                rq = route.query ?? {}
            }

            if (rq && 'no-cache' in rq) {
                const separator = uri.includes('?') ? '&' : '?'
                uri += `${separator}no-cache=${Date.now()}`
            }
        }

        const beforeFetchStamp = Date.now() / 1000

        // SSR: fetch directly from CMS data-api (credentials available from headers)
        if (import.meta.server && serverConfig) {
            const dataApiEndpoint = replace(serverConfig.endpoint, '/api', '/data-api')

            const headers: Record<string, string> = {
                'Authorization': `Bearer ${serverConfig.token}`,
                'Cache-Control': 'public',
            }

            if (isPreview.value) {
                headers['X-Craft-Token'] = previewToken.value
                headers['X-Craft-Preview-Token'] = livePreviewToken.value
            }

            if (options.server === false) {
                return { data: null }
            }

            const { data, error } = await useFetch(`${dataApiEndpoint}/${uri}`, {
                ...options,
                headers,
                query: variables,
            })

            if (error.value) {
                // Log the original error for debugging purposes
                console.error('CMS data request failed', error)

                throw createError({
                    statusCode: error.value.statusCode ?? 502,
                    statusMessage: error.value.message ?? 'CMS data request failed',
                })
            }

            if (data.value?.errors) {
                // if we are in dev mode, throw a hard error 
                if (import.meta.dev) {
                    throw createError({
                        statusCode: 500,
                        statusMessage: 'CMS API returned partial errors.',
                        data: data.value?.errors
                    })
                } else {
                    // fail silently in production but log the errors for debugging
                    console.error('CMS API returned partial errors:', data.value?.errors)
                }

            }
            console.groupCollapsed(`${options.key} query in`, `${(Date.now() / 1000 - beforeFetchStamp).toFixed(2)}s`)
            console.groupEnd()

            return { data }
        }

        // CSR: fetch via same-origin server proxy (token stays server-side)
        const headers: Record<string, string> = {
            'Cache-Control': 'public',
            'X-CSRF-Token': csrfToken.value,
        }

        if (isPreview.value) {
            headers['X-Craft-Token'] = previewToken.value
            headers['X-Craft-Preview-Token'] = livePreviewToken.value
        }

        // if server = false, use $fetch (client-only)
        if (options.server === false) {
            const response = await fetchWithCsrfRetry(`/api/cms/${uri}`, {
                ...options,
                headers,
                query: variables,
            })

            return { data: response.data }
        }

        const { data } = await useFetchWithCsrfRetry(`/api/cms/${uri}`, {
            ...options,
            headers,
            query: variables,
        })

        return { data }
    }

    return {
        apiUrl: cmsApiUrl.value,
        adminUrl: adminUrl.value,
        craftUrl: craftUrl.value,
        query,
        cmsQuery
    }
}
